// ManDana — a live session for one visible, interactive desk.
(function (g: any) {
  'use strict';

  /* The transport's name is asked, not spelled — the seam test owns the one
     place `cf` may appear, and a desk that names its own relay is the
     swap-to-Nostr day already lost. */
  const HEARTBEAT_MS = 5000;
  const EXPIRE_MS = 12000;
  const SCENE_FRAME_MS = 33;
  const MOTION_FRAME_MS = 1000 / 60;
  const CONGESTED_FRAME_MS = 1000 / 30;
  const CONGESTION_COOLDOWN_MS = 2000;
  const SCENE_FINAL_MS = 180;
  const SNAPSHOT_ELEMENTS = 64;
  const SCENE_ELEMENTS = 64;
  /* Live desk sync is deliberately a per-tab session, not a remembered
     preference. A person turns it on from this desk's toolbar and a fresh app
     starts quiet again. Durable sync is independent of this switch. */
  const LIVE_IDLE_MS = 3 * 60 * 1000;

  const sessions = new Map<string, any>();
  let liveDesk = '';
  let liveLastActivity = 0;
  let liveTimer: any = null;

  function liveSay(reason: string, previousDesk = '') {
    if (typeof g.dispatchEvent !== 'function' || typeof g.CustomEvent !== 'function') return;
    g.dispatchEvent(new g.CustomEvent('mdcollabcontrol', { detail: {
      desk: liveDesk, previousDesk, enabled: !!liveDesk, reason, idleMs: LIVE_IDLE_MS,
    } }));
  }

  function liveSchedule() {
    if (liveTimer) g.clearTimeout(liveTimer);
    liveTimer = null;
    if (!liveDesk) return;
    const remaining = Math.max(0, LIVE_IDLE_MS - (Date.now() - liveLastActivity));
    liveTimer = g.setTimeout(() => {
      /* Activity only moves a timestamp; it does not churn a timer on every
         pointer frame. When the old deadline arrives it either turns the room
         off or sleeps exactly the remaining distance to the last touch. */
      if (Date.now() - liveLastActivity < LIVE_IDLE_MS) { liveSchedule(); return; }
      const previousDesk = liveDesk;
      liveDesk = '';
      liveTimer = null;
      liveSay('idle', previousDesk);
    }, remaining);
  }

  function mdCollabEnabled(desk?: string) {
    return !!liveDesk && (!desk || desk === liveDesk);
  }

  function mdCollabSetEnabled(desk: string, enabled: boolean) {
    if (!/^[a-z0-9]{10}$/.test(desk)) return { ok: false, error: 'bad_desk' };
    const previousDesk = liveDesk;
    if (!enabled) {
      if (liveDesk !== desk) return { ok: true, enabled: false };
      liveDesk = '';
      if (liveTimer) g.clearTimeout(liveTimer);
      liveTimer = null;
      liveSay('manual', previousDesk);
      return { ok: true, enabled: false };
    }
    liveDesk = desk;
    liveLastActivity = Date.now();
    liveSchedule();
    liveSay('manual', previousDesk);
    return { ok: true, enabled: true, idleMs: LIVE_IDLE_MS };
  }

  function mdCollabTouch(desk: string) {
    if (desk !== liveDesk) return false;
    liveLastActivity = Date.now();
    return true;
  }

  function randomHex(bytes: number): string {
    if (!g.crypto || typeof g.crypto.getRandomValues !== 'function') return '';
    const data = new Uint8Array(bytes);
    g.crypto.getRandomValues(data);
    return [...data].map((n) => n.toString(16).padStart(2, '0')).join('');
  }

  function actorId(): string {
    /* An actor is one LIVE tenure in a room. A reconnect inside that tenure
       keeps the actor and sequence counter; leaving (hidden tab, idle switch,
       desk change) ends it, and the next join mints a new epoch.

       Reusing an actor after leave while resetting seq to zero makes every
       peer correctly reject the new frames as replays of the old session. In
       practice that looked like Live Sync was on while paste, delete and wipe
       all did nothing. A fresh actor makes the epoch explicit and also keeps
       delayed frames from a dead socket out of the new session. */
    return randomHex(16);
  }

  function elementStamp(element: any): string {
    return element
      ? `${element.id}:${Number(element.version) || 0}:${Number(element.versionNonce) || 0}:${element.isDeleted ? 1 : 0}`
      : '';
  }

  /* The board's local/remote boundary as a pure, testable operation. `known`
     records every element version the canvas has reported; `remote` contains
     only versions reconciliation accepted from a peer. Reading a remote version
     once prevents an echo without muting the next genuine local mutation. */
  function mdCollabSceneChanges(elements: any[], known: Map<string, string>, remote: Map<string, string>) {
    if (!Array.isArray(elements) || !(known instanceof Map) || !(remote instanceof Map)) return [];
    const changes: any[] = [];
    for (const element of elements) {
      if (!element || typeof element.id !== 'string' || !element.id) continue;
      const version = elementStamp(element);
      if (known.get(element.id) === version) continue;
      known.set(element.id, version);
      if (remote.get(element.id) === version) {
        remote.delete(element.id);
        continue;
      }
      changes.push(element);
    }
    return changes;
  }

  function deviceName(dev: string): string {
    try {
      const devices = typeof g.mdDevicesLinked === 'function' ? g.mdDevicesLinked() : {};
      return devices && devices[dev] && devices[dev].name
        ? String(devices[dev].name).slice(0, 80)
        : (typeof g.mdDeviceNameGuess === 'function' ? g.mdDeviceNameGuess() : 'Device');
    } catch { return 'Device'; }
  }

  function frameFor(session: any, fields: any) {
    return {
      v: g.MD_COLLAB_VERSION || 1,
      desk: session.desk,
      dev: session.dev,
      actor: session.actor,
      seq: ++session.seq,
      sentAt: Date.now(),
      ...fields,
    };
  }

  async function wire(session: any, fields: any, delivery = 'reliable', coalesceKey = '') {
    if (session.closed) return { ok: false, error: 'left' };
    const frame = frameFor(session, fields);
    const bytes = g.mdCollabEncode(frame);
    if (!bytes) return { ok: false, error: 'frame_too_large' };
    session.stats.bytesOut += bytes.byteLength;
    if (fields.t === 'scene' && fields.phase === 'final') {
      session.awaitingAck.set(frame.seq, Date.now());
      if (session.awaitingAck.size > 128) session.awaitingAck.delete(session.awaitingAck.keys().next().value);
    }
    try {
      const result = await session.transport.publish(session.room, bytes, { delivery, coalesceKey });
      if (delivery === 'ephemeral' && result && result.coalesced) {
        session.stats.droppedEphemeral += 1;
        session.congestedUntil = Date.now() + CONGESTION_COOLDOWN_MS;
      }
      return result;
    } catch (err: any) {
      return { ok: false, error: (err && err.message) || String(err) };
    }
  }

  function peerList(session: any) {
    return [...session.peers.values()]
      .sort((a, b) => a.name.localeCompare(b.name) || a.actor.localeCompare(b.actor));
  }

  function announcePeers(session: any) {
    try { session.handlers.onPeers && session.handlers.onPeers(peerList(session)); } catch { /* view left */ }
  }

  function rememberPeer(session: any, frame: any) {
    const previous = session.peers.get(frame.actor) || {};
    session.peers.set(frame.actor, {
      ...previous,
      actor: frame.actor,
      deviceId: frame.dev,
      name: frame.name || previous.name || 'Device',
      lastSeen: Date.now(),
      pointer: frame.t === 'pointer' ? frame.pointer : previous.pointer,
      button: frame.t === 'pointer' ? frame.button : previous.button,
      selected: frame.t === 'pointer' ? frame.selected : (previous.selected || []),
      tool: frame.t === 'pointer' ? frame.tool : previous.tool,
    });
  }

  function sceneChunks(elements: any[], size = SCENE_ELEMENTS) {
    const chunks: any[][] = [];
    for (let i = 0; i < elements.length; i += size) chunks.push(elements.slice(i, i + size));
    if (!chunks.length) chunks.push([]);
    return chunks;
  }

  function publishSceneFrames(session: any, elements: any[], phase: string) {
    const chunks = sceneChunks(elements, phase === 'snapshot' ? SNAPSHOT_ELEMENTS : SCENE_ELEMENTS);
    return Promise.all(chunks.map((part, index) => wire(session, {
      t: 'scene', phase, part: index, total: chunks.length, elements: part,
    }, phase === 'progress' ? 'ephemeral' : 'reliable',
    phase === 'progress' ? `${session.actor}:scene:${index}` : '')));
  }

  function motionFrameMs(session: any) {
    return Date.now() < session.congestedUntil ? CONGESTED_FRAME_MS : MOTION_FRAME_MS;
  }

  function strokeStart(element: any) {
    return {
      ...element,
      points: Array.isArray(element.points) ? element.points.slice(0, 1) : [],
      pressures: Array.isArray(element.pressures) ? element.pressures.slice(0, 1) : [],
      lastCommittedPoint: null,
    };
  }

  function strokeTail(element: any) {
    return {
      t: 'stroke', phase: 'tail', id: element.id, from: 1,
      /* Cumulative from the reliable first point. The transport may replace
         an older tail under pressure without creating a gap on the receiver. */
      points: Array.isArray(element.points) ? element.points.slice(1) : [],
      pressures: Array.isArray(element.pressures) ? element.pressures.slice(1) : [],
      x: Number(element.x) || 0, y: Number(element.y) || 0,
      width: Number(element.width) || 0, height: Number(element.height) || 0,
      version: Math.max(0, Number(element.version) || 0),
      versionNonce: Math.max(0, Number(element.versionNonce) || 0),
      lastCommittedPoint: Array.isArray(element.lastCommittedPoint)
        ? element.lastCommittedPoint : null,
    };
  }

  function flushStroke(session: any, id: string) {
    const stroke = session.localStrokes.get(id);
    if (!stroke || session.closed) return Promise.resolve({ ok: false, error: 'left' });
    if (stroke.timer) { g.clearTimeout(stroke.timer); stroke.timer = null; }
    const element = stroke.latest;
    stroke.latest = null;
    if (!element) return Promise.resolve({ ok: true, empty: true });
    stroke.lastSentAt = Date.now();
    session.stats.strokeFramesOut += 1;
    return Promise.resolve(stroke.started).then(() => wire(session, strokeTail(element),
      'ephemeral', `${session.actor}:stroke:${id}`));
  }

  function scheduleStroke(session: any, stroke: any) {
    if (stroke.timer || !stroke.latest || stroke.latest.points.length <= 1) return;
    const wait = Math.max(0, motionFrameMs(session) - (Date.now() - stroke.lastSentAt));
    stroke.timer = g.setTimeout(() => { void flushStroke(session, stroke.id); }, wait);
  }

  function finishLocalStrokes(session: any, elements: any[]) {
    for (const element of elements) {
      const stroke = element && session.localStrokes.get(element.id);
      if (!stroke) continue;
      if (stroke.timer) g.clearTimeout(stroke.timer);
      session.localStrokes.delete(element.id);
    }
  }

  function applyStroke(session: any, frame: any) {
    const key = `${frame.actor}:${frame.phase === 'start' ? frame.element.id : frame.id}`;
    if (frame.phase === 'start') {
      const element = { ...frame.element,
        points: [...frame.element.points], pressures: [...(frame.element.pressures || [])] };
      session.remoteStrokes.set(key, element);
      session.stats.strokeFramesIn += 1;
      try { session.handlers.onScene && session.handlers.onScene([element], frame); } catch { /* view left */ }
      return;
    }
    const previous = session.remoteStrokes.get(key);
    if (!previous || frame.from > previous.points.length) return;
    const element = {
      ...previous,
      id: frame.id,
      x: frame.x, y: frame.y, width: frame.width, height: frame.height,
      version: frame.version, versionNonce: frame.versionNonce,
      lastCommittedPoint: frame.lastCommittedPoint,
      points: previous.points.slice(0, frame.from).concat(frame.points),
      pressures: (previous.pressures || []).slice(0, frame.from).concat(frame.pressures || []),
    };
    session.remoteStrokes.set(key, element);
    session.stats.strokeFramesIn += 1;
    session.stats.strokeTailPoints += frame.points.length;
    try { session.handlers.onScene && session.handlers.onScene([element], frame); } catch { /* view left */ }
  }

  function publishSnapshot(session: any) {
    let elements: any[] = [];
    try {
      const got = session.handlers.snapshot && session.handlers.snapshot();
      if (Array.isArray(got)) elements = got;
    } catch { elements = []; }
    void publishSceneFrames(session, elements, 'snapshot');
    try {
      /* assets() may answer synchronously or with a promise — a desk's file
         references live in IndexedDB, which cannot be read on the wire's turn.
         The scene is already sent either way; the names follow when they are
         known. */
      const answered = session.handlers.assets && session.handlers.assets();
      void Promise.resolve(answered).then((assets) => {
        if (session.closed || !Array.isArray(assets)) return;
        assets.forEach((asset) => {
          if (asset && asset.fileId && asset.hash) void wire(session, { t: 'asset', ...asset }, 'reliable');
        });
      });
    } catch { /* a scene is still useful without an asset list */ }
  }

  function retryAssets(session: any) {
    for (const asset of session.pendingAssets.values()) void downloadAsset(session, asset);
  }

  /* THROUGH md-blobs, NOT THROUGH THE TRANSPORT. This asked the store for a
     hash and handed whatever came back to the canvas, which made the live desk
     the fifth module in this app keeping bytes — and md-blobs.ts opens with why
     that is the wrong number: every one of them has to independently remember
     to verify a download, and the one that forgets is wrong about that picture
     for ever with no way to find out. The name IS the checksum. A proxy, a
     truncated read, or a Blossom server run by a stranger can answer with
     different bytes under the hash we asked for, and only the door checks.

     Two things follow from going through it, and both are why this is the door
     rather than a check copied to a fifth place: the bytes are KEPT, so
     rejoining the desk does not re-download the picture, and a chunked upload
     is resolved on the way past instead of arriving as a manifest the canvas
     would try to draw.

     WITHOUT THE DOOR THERE IS NO ANSWER, only an unverified one. So a build
     that has not loaded md-blobs reports the miss it honestly has, rather than
     degrading to trusting a stranger — the one place in this file where the
     absent-door fallback is refusal instead of the older behaviour. */
  async function downloadAsset(session: any, asset: any) {
    if (session.closed || session.downloading.has(asset.hash)) return;
    session.downloading.add(asset.hash);
    let bytes: any = null;
    try {
      bytes = typeof g.mdBlobGet === 'function' ? await g.mdBlobGet(asset.hash) : null;
    } catch { bytes = null; }
    session.downloading.delete(asset.hash);
    if (!(bytes instanceof Uint8Array)) {
      session.pendingAssets.set(asset.hash, asset);
      try { session.handlers.onAsset && session.handlers.onAsset(asset, null); } catch { /* view left */ }
      return;
    }
    session.pendingAssets.delete(asset.hash);
    try { session.handlers.onAsset && session.handlers.onAsset(asset, bytes); } catch { /* view left */ }
  }

  function receive(session: any, bytes: Uint8Array) {
    if (session.closed) return;
    session.stats.bytesIn += bytes.byteLength;
    const frame = g.mdCollabDecode(bytes);
    if (!frame || frame.desk !== session.desk || frame.actor === session.actor) {
      if (!frame) session.stats.malformed += 1;
      return;
    }
    const key = `${frame.actor}:${frame.t}`;
    const last = session.sequences.get(key) || -1;
    if (frame.seq <= last) return;
    session.sequences.set(key, frame.seq);

    if (frame.t === 'ack') {
      const sent = session.awaitingAck.get(frame.forSeq);
      if (sent) {
        session.stats.ackRtt = Date.now() - sent;
        session.awaitingAck.delete(frame.forSeq);
      }
      return;
    }

    if (frame.t === 'presence' && frame.phase === 'leave') {
      session.peers.delete(frame.actor);
      announcePeers(session);
      return;
    }
    rememberPeer(session, frame);
    announcePeers(session);

    if (frame.t === 'presence') return;
    if (frame.t === 'snapshot-request') { publishSnapshot(session); return; }
    if (frame.t === 'pointer') {
      try { session.handlers.onPointer && session.handlers.onPointer(frame, peerList(session)); } catch { /* view left */ }
      return;
    }
    if (frame.t === 'stroke') {
      const started = Date.now();
      applyStroke(session, frame);
      session.stats.remoteApplyMs = Date.now() - started;
      session.stats.lastOneWayMs = Math.max(0, Date.now() - frame.sentAt);
      return;
    }
    if (frame.t === 'asset') {
      session.pendingAssets.set(frame.hash, frame);
      void downloadAsset(session, frame);
      return;
    }
    if (frame.t === 'scene') {
      for (const element of frame.elements) {
        if (element && element.id) session.remoteStrokes.delete(`${frame.actor}:${element.id}`);
      }
      const started = Date.now();
      try { session.handlers.onScene && session.handlers.onScene(frame.elements, frame); } catch { /* view left */ }
      session.stats.remoteApplyMs = Date.now() - started;
      session.stats.lastOneWayMs = Math.max(0, Date.now() - frame.sentAt);
      if (frame.phase === 'final') {
        void wire(session, { t: 'ack', forSeq: frame.seq }, 'reliable');
      }
    }
  }

  function connectionState(session: any, state: any) {
    if (session.closed || !state) return;
    if (state.state === 'retrying') session.stats.reconnects += 1;
    if (Number.isFinite(state.droppedEphemeral)) session.stats.droppedEphemeral = state.droppedEphemeral;
    session.state = state.state;
    try { session.handlers.onState && session.handlers.onState(state); } catch { /* view left */ }
    if (state.state === 'open') {
      void wire(session, { t: 'presence', phase: 'join', name: session.name }, 'reliable');
      void wire(session, { t: 'snapshot-request' }, 'reliable');
      retryAssets(session);
    }
  }

  function flushScene(session: any, phase: string) {
    if (session.sceneTimer) { g.clearTimeout(session.sceneTimer); session.sceneTimer = null; }
    if (phase === 'final' && session.finalTimer) { g.clearTimeout(session.finalTimer); session.finalTimer = null; }
    const source = phase === 'final' ? session.canonicalElements : session.pendingElements;
    const elements = [...source.values()];
    source.clear();
    if (phase === 'final') session.pendingElements.clear();
    if (!elements.length) return Promise.resolve({ ok: true, empty: true });
    if (phase === 'final') finishLocalStrokes(session, elements);
    return publishSceneFrames(session, elements, phase);
  }

  /* In the app there is exactly one session per desk per tab, which is what
     the guard below protects. A two-device test drives two tabs through one
     global, so it scopes the guard by device — the option names the tab, and
     in the browser it is simply absent. */
  function sessionKey(desk: string, options: any): string {
    return options && typeof options.scope === 'string' && options.scope
      ? `${options.scope}:${desk}` : desk;
  }

  async function mdCollabJoin(desk: string, handlers: any = {}, options: any = {}) {
    if (!/^[a-z0-9]{10}$/.test(desk)) return { ok: false, error: 'bad_desk' };
    const key = sessionKey(desk, options);
    if (sessions.has(key)) return { ok: false, error: 'desk_already_joined' };
    const dev = typeof g.mdDeviceId === 'function' ? g.mdDeviceId() : '';
    const actor = actorId();
    if (!/^[0-9a-f]{16}$/.test(dev) || !actor) return { ok: false, error: 'no_identity' };
    const transportName = options.transport
      || (typeof g.mdTransportName === 'function' ? g.mdTransportName() : undefined);
    const opened = await g.mdTransportOpen(transportName);
    if (!opened || !opened.ok) return { ok: false, error: opened && opened.error };

    const session: any = {
      desk, key, room: `self/desk/${desk}`, dev, actor, name: deviceName(dev), handlers,
      transport: opened.transport, sub: null, seq: 0, closed: false, state: 'connecting',
      peers: new Map(), sequences: new Map(), pendingElements: new Map(), canonicalElements: new Map(),
      localStrokes: new Map(), remoteStrokes: new Map(), sceneTimer: null, finalTimer: null,
      pendingPointer: null, pointerTimer: null, pointerLastSentAt: null, pointerButton: 'up',
      congestedUntil: 0,
      pendingAssets: new Map(), downloading: new Set(), awaitingAck: new Map(),
      stats: { bytesIn: 0, bytesOut: 0, droppedEphemeral: 0, reconnects: 0,
        malformed: 0, remoteApplyMs: 0, lastOneWayMs: 0, ackRtt: 0,
        strokeFramesOut: 0, strokeFramesIn: 0, strokeTailPoints: 0 },
    };
    sessions.set(key, session);
    let sub: any;
    try {
      sub = await session.transport.subscribe(session.room, (bytes: Uint8Array) => receive(session, bytes), {
        onState: (state: any) => connectionState(session, state),
      });
    } catch (err: any) { sub = { ok: false, error: (err && err.message) || String(err) }; }
    if (!sub || !sub.ok) {
      sessions.delete(key);
      try { session.transport.close(); } catch { /* no socket */ }
      return { ok: false, error: sub && sub.error };
    }
    session.sub = sub;

    const heartbeat = g.setInterval(() => {
      void wire(session, { t: 'presence', phase: 'heartbeat', name: session.name }, 'ephemeral', `${actor}:presence`);
    }, HEARTBEAT_MS);
    const expiry = g.setInterval(() => {
      const now = Date.now();
      let changed = false;
      for (const [id, peer] of session.peers) {
        if (now - peer.lastSeen > EXPIRE_MS) { session.peers.delete(id); changed = true; }
      }
      if (changed) announcePeers(session);
    }, 1000);
    const online = () => retryAssets(session);
    if (typeof g.addEventListener === 'function') g.addEventListener('online', online);

    /* Some transport fakes do not expose connection state. The join and
       snapshot request are safe to queue and make that contract optional. */
    void wire(session, { t: 'presence', phase: 'join', name: session.name }, 'reliable');
    void wire(session, { t: 'snapshot-request' }, 'reliable');

    const api: any = {
      ok: true,
      publishScene(elements: any[], options: any = {}) {
        if (!Array.isArray(elements) || session.closed) return { ok: false };
        for (const element of elements) if (element && element.id) {
          session.pendingElements.set(element.id, element);
          session.canonicalElements.set(element.id, element);
        }
        if (options.final) {
          finishLocalStrokes(session, elements);
          return flushScene(session, 'final');
        }
        if (!session.sceneTimer) session.sceneTimer = g.setTimeout(() => flushScene(session, 'progress'), SCENE_FRAME_MS);
        if (session.finalTimer) g.clearTimeout(session.finalTimer);
        session.finalTimer = g.setTimeout(() => flushScene(session, 'final'), SCENE_FINAL_MS);
        return { ok: true, queued: true };
      },
      publishStroke(element: any) {
        if (!element || element.type !== 'freedraw' || !element.id
          || !Array.isArray(element.points) || !element.points.length || session.closed) {
          return { ok: false, error: 'bad_stroke' };
        }
        let stroke = session.localStrokes.get(element.id);
        if (!stroke) {
          stroke = {
            id: element.id, latest: null, timer: null, lastSentAt: Date.now(),
            started: wire(session, { t: 'stroke', phase: 'start', element: strokeStart(element) },
              'reliable'),
          };
          session.localStrokes.set(element.id, stroke);
        }
        stroke.latest = element;
        session.canonicalElements.set(element.id, element);
        scheduleStroke(session, stroke);
        /* Pointer-up sends the canonical immediately. This quiet-time final is
           the safety net for pointercancel, a browser gesture interruption,
           or hardware that disappears without an up event. */
        if (session.finalTimer) g.clearTimeout(session.finalTimer);
        session.finalTimer = g.setTimeout(() => { void flushScene(session, 'final'); }, SCENE_FINAL_MS);
        return { ok: true, queued: true };
      },
      publishPointer(pointer: any) {
        if (session.closed) return { ok: false };
        const fields = {
          t: 'pointer', pointer: { x: Number(pointer && pointer.x) || 0, y: Number(pointer && pointer.y) || 0 },
          button: pointer && pointer.button === 'down' ? 'down' : 'up',
          tool: String((pointer && pointer.tool) || '').slice(0, 32),
          selected: Array.isArray(pointer && pointer.selected) ? pointer.selected : [],
        };
        session.pendingPointer = fields;
        const transition = fields.button !== session.pointerButton;
        session.pointerButton = fields.button;
        const flush = () => {
          if (session.pointerTimer) { g.clearTimeout(session.pointerTimer); session.pointerTimer = null; }
          const latest = session.pendingPointer;
          session.pendingPointer = null;
          if (!latest) return Promise.resolve({ ok: true, empty: true });
          session.pointerLastSentAt = Date.now();
          return wire(session, latest, 'ephemeral', `${actor}:pointer`);
        };
        if (transition || session.pointerLastSentAt === null) return flush();
        if (!session.pointerTimer) {
          const wait = Math.max(0, motionFrameMs(session)
            - (Date.now() - session.pointerLastSentAt));
          session.pointerTimer = g.setTimeout(() => { void flush(); }, wait);
        }
        return { ok: true, queued: true };
      },
      async publishAsset(file: any, bytes: Uint8Array) {
        if (!file || !(bytes instanceof Uint8Array)) return { ok: false, error: 'bad_asset' };
        /* The same door the download goes through, and for the third reason
           md-blobs.ts gives: a hold. These bytes belong to THIS desk, so the
           put is made under that reference and the store's collector knows the
           picture is spoken for. Sent raw, the upload was also a second copy of
           bytes the paste path had already put there — the cache answers now
           instead. See downloadAsset. */
        const put = typeof g.mdBlobPut === 'function'
          ? await g.mdBlobPut(bytes, { type: String(file.mimeType || ''), ref: `board:${session.desk}` })
          : await session.transport.blobPut(bytes);
        if (!put || !put.ok) return put;
        await wire(session, {
          t: 'asset', fileId: String(file.fileId || file.id || ''), hash: put.hash,
          mimeType: String(file.mimeType || ''), size: bytes.byteLength,
        }, 'reliable');
        return { ok: true, hash: put.hash };
      },
      diagnostics() { return { ...session.stats, state: session.state, peers: session.peers.size }; },
      async leave() {
        if (session.closed) return;
        const final = flushScene(session, 'final');
        const goodbye = wire(session, { t: 'presence', phase: 'leave', name: session.name }, 'reliable');
        session.closed = true;
        g.clearInterval(heartbeat); g.clearInterval(expiry);
        if (session.sceneTimer) g.clearTimeout(session.sceneTimer);
        if (session.finalTimer) g.clearTimeout(session.finalTimer);
        if (session.pointerTimer) g.clearTimeout(session.pointerTimer);
        for (const stroke of session.localStrokes.values()) if (stroke.timer) g.clearTimeout(stroke.timer);
        if (typeof g.removeEventListener === 'function') g.removeEventListener('online', online);
        try { session.sub.unsubscribe(); } catch { /* already closed */ }
        try { session.transport.close(); } catch { /* already closed */ }
        sessions.delete(session.key);
        await Promise.allSettled([final, goodbye]);
      },
    };
    return api;
  }

  function mdCollabDiagnostics() {
    return [...sessions.values()].map((s) => ({ desk: s.desk, ...s.stats, state: s.state, peers: s.peers.size }));
  }

  Object.assign(g, { mdCollabJoin, mdCollabDiagnostics, mdCollabEnabled, mdCollabSceneChanges,
    mdCollabSetEnabled, mdCollabTouch, MD_COLLAB_IDLE_MS: LIVE_IDLE_MS });
})(typeof window !== 'undefined' ? window : globalThis);
